/**
 * DashboardDesignPreview
 * Public route: /design-preview
 *
 * Three institutional design directions for DrawReady. The DrawReady brand
 * (blue gradient "D" logo, slate sidebar, blue primary color) stays
 * CONSTANT across all three. Only the paper, typography, and secondary
 * accent change. This keeps the product unmistakably DrawReady while
 * elevating the visual register from "AI-generated SaaS" to "institutional
 * finance product".
 */
import React, { useState, useEffect } from 'react';
import { Info, LayoutDashboard, FolderOpen, FileBarChart, ShieldCheck, Settings as SettingsIcon, User as UserIcon } from 'lucide-react';

// ---------- Google Fonts (loaded once) ----------
const FONT_LINK_ID = 'design-preview-fonts';
const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&family=Source+Serif+4:ital,opsz,wght@0,8..60,400;0,8..60,600;0,8..60,700;1,8..60,400;1,8..60,600&family=Inter+Tight:wght@400;500;600&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap';

const useDesignFonts = () => {
  useEffect(() => {
    if (document.getElementById(FONT_LINK_ID)) return;
    const link = document.createElement('link');
    link.id = FONT_LINK_ID;
    link.rel = 'stylesheet';
    link.href = FONT_HREF;
    document.head.appendChild(link);
  }, []);
};

// ---------- Mock data ----------
const MOCK_PROJECTS = [
  { id: 'p1', name: '1615 38th St SE',  address: '1615 38th St SE · Owner-listed',     loan: 360_000,   drawn: 122_654,  rate: 7.125, term: 12,   ext: false, score: 100, daily: 24.28 },
  { id: 'p2', name: 'Ellis Row',         address: '1319-1333 Ellis Row',                loan: 1_500_000, drawn: 950_000,  rate: 7.85,  term: 24,   ext: false, score: 90,  daily: 207.18 },
  { id: 'p3', name: '211 15th ST',       address: '211 15th ST SE Cedar Rapids, IA',    loan: 800_000,   drawn: 8_000,    rate: 7.50,  term: 18,   ext: true,  score: 60,  daily: 1.67 },
  { id: 'p4', name: '408 4th Ave',       address: '408 4th Ave · Brokerage: Coldwell',  loan: 183_000,   drawn: 0,        rate: 8.00,  term: 12,   ext: false, score: 50,  daily: 0 },
  { id: 'p5', name: '515 Cedar Rapids',  address: '515 1st Ave SW Cedar Rapids, IA',    loan: 0,         drawn: 0,        rate: 0,     term: null, ext: false, score: 55, daily: 0 },
];

const MOCK_CONTRACTORS = [
  { id: 'c1', name: 'CHL Construction',     trade: 'framing',         w9: 'awaiting',  coi: 'awaiting',  lien: 'none' },
  { id: 'c2', name: 'Block 7',              trade: 'general',         w9: 'approved',  coi: 'approved',  lien: 'none' },
  { id: 'c3', name: 'Brain Engineering',    trade: 'engineering',     w9: 'awaiting',  coi: 'awaiting',  lien: 'none' },
  { id: 'c4', name: "Steve's Erosion Ctrl", trade: 'erosion control', w9: 'awaiting',  coi: 'awaiting',  lien: 'none' },
  { id: 'c5', name: 'JPM',                  trade: 'excavation',      w9: 'approved',  coi: 'approved',  lien: 'none' },
  { id: 'c6', name: 'Mid-City Mechanical',  trade: 'HVAC',            w9: 'approved',  coi: 'expiring',  lien: 'submitted' },
];

const KPIS = {
  available: '$1.5M',           availableSub: 'Across 5 active loans',
  dailyInterest: '$233.13',     dailyInterestSub: 'Total carrying cost / day',
  bankReady: '71',              bankReadySub: 'Portfolio score / 100',
  compliance: '2 / 6',          complianceSub: 'Contractors fully approved',
};

const TOOLTIP_COPY =
  'Daily Interest = amount drawn × (current rate ÷ 100) ÷ 360. Carrying cost your loan accrues each day on already-drawn funds. Matches the Daily Interest figure on each project\u2019s Loan Details page.';

// ---------- DrawReady Brand Constants ----------
// These are CONSTANT across all themes — they ARE the brand.
const DR_BLUE       = '#2563EB';   // Primary action / brand
const DR_BLUE_DARK  = '#1D4ED8';   // Hover / pressed
const DR_BLUE_LIGHT = '#3B82F6';   // Logo gradient start
const DR_SIDEBAR_BG = '#0F172A';   // slate-900
const DR_SIDEBAR_BORDER = '#334155'; // slate-700

// ---------- DrawReady Sidebar (shared across all themes) ----------
const DrawReadySidebar = ({ activeTheme }) => {
  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', active: true },
    { icon: FolderOpen,      label: 'Project Hub' },
    { icon: FileBarChart,    label: 'Reports' },
    { icon: ShieldCheck,     label: 'Compliance' },
    { icon: SettingsIcon,    label: 'Settings' },
    { icon: UserIcon,        label: 'Profile' },
  ];
  return (
    <aside
      className="w-64 flex-shrink-0 flex flex-col"
      style={{ background: DR_SIDEBAR_BG, color: '#fff', minHeight: '100%' }}
    >
      <div className="p-4" style={{ borderBottom: `1px solid ${DR_SIDEBAR_BORDER}` }}>
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: `linear-gradient(135deg, ${DR_BLUE_LIGHT}, ${DR_BLUE_DARK})` }}
          >
            <span className="text-white font-bold text-lg">D</span>
          </div>
          <div>
            <h1 className="font-bold text-lg whitespace-nowrap">DrawReady</h1>
            <p className="text-xs text-slate-400">Construction Finance</p>
          </div>
        </div>
      </div>
      <nav className="flex-1 py-4">
        <ul className="space-y-1 px-3">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.label}>
                <div
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-default"
                  style={{
                    background: item.active ? DR_BLUE : 'transparent',
                    color: item.active ? '#fff' : '#cbd5e1',
                  }}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-sm font-medium">{item.label}</span>
                </div>
              </li>
            );
          })}
        </ul>
      </nav>
      <div className="p-4 text-[10px] text-slate-500 uppercase tracking-wider" style={{ borderTop: `1px solid ${DR_SIDEBAR_BORDER}` }}>
        Theme: {activeTheme}
      </div>
    </aside>
  );
};

// ---------- Tooltip ----------
const InfoTip = ({ text, color = '#6B7280', hover = '#111827' }) => {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen((s) => !s)}
        style={{ color: open ? hover : color }}
        className="ml-1.5 inline-flex items-center transition-colors"
        aria-label="What is Daily Interest?"
      >
        <Info className="w-3.5 h-3.5" />
      </button>
      {open && (
        <span
          role="tooltip"
          className="absolute z-50 left-1/2 -translate-x-1/2 top-full mt-1.5 w-72 px-3 py-2 text-xs leading-snug bg-[#0F172A] text-white shadow-lg"
          style={{ borderRadius: 4 }}
        >
          {text}
        </span>
      )}
    </span>
  );
};

// =============================================================
// THEME A — JPM Private Bank (DrawReady blue + brass figure accent)
// =============================================================
const ThemeJPM = () => {
  const c = {
    bg: '#F5F1EA', surface: '#FFFFFF', ink: '#0A1F3D', muted: '#4A5568',
    accent: DR_BLUE, accentDark: DR_BLUE_DARK,
    secondary: '#B8860B', // brass — used SPARINGLY for figure emphasis
    success: '#2E7D32', danger: '#D32F2F', line: 'rgba(10,31,61,0.12)',
  };
  const fontH = "'Source Serif 4', serif";
  const fontB = "'Inter Tight', sans-serif";
  const fontM = "'JetBrains Mono', monospace";

  const KpiBlock = ({ label, value, sub, info, highlight }) => (
    <div style={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 2 }} className="p-5">
      <p style={{ fontFamily: fontB, color: c.muted }} className="text-[10px] font-semibold uppercase tracking-[0.18em] mb-2 inline-flex items-center">
        {label}
        {info && <InfoTip text={info} color={c.muted} hover={c.ink} />}
      </p>
      <hr style={{ borderColor: c.line }} className="mb-3" />
      <p style={{ fontFamily: fontM, color: highlight ? c.secondary : c.ink }} className="text-3xl font-medium tabular-nums">{value}</p>
      {sub && <p style={{ fontFamily: fontB, color: c.muted }} className="text-[11px] mt-1.5">{sub}</p>}
    </div>
  );

  const Pill = ({ label, status }) => {
    const styles = {
      approved:  { color: c.success, label: '✓' },
      submitted: { color: c.accent,  label: 'Sub' },
      expiring:  { color: '#ED6C02', label: 'Exp' },
      awaiting:  { color: c.danger,  label: '!' },
      none:      { color: c.muted,   label: '—' },
    }[status] || { color: c.muted, label: '—' };
    return (
      <span style={{ borderColor: styles.color, color: styles.color, fontFamily: fontM, borderRadius: 2 }} className="px-1.5 py-0.5 text-[9px] uppercase tracking-wider border bg-white">
        {label} {styles.label}
      </span>
    );
  };

  return (
    <div style={{ background: c.bg, fontFamily: fontB, color: c.ink }} className="px-8 py-8 min-h-full">
      <header className="flex justify-between items-end pb-4 mb-8" style={{ borderBottom: `2px solid ${c.accent}` }}>
        <div>
          <p style={{ fontFamily: fontB, color: c.muted }} className="text-[10px] uppercase tracking-[0.3em] mb-1">
            Mods Enterprises · Portfolio Statement · {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
          <h1 style={{ fontFamily: fontH, color: c.ink }} className="text-4xl font-semibold leading-none">
            Command Center
          </h1>
        </div>
        <div className="flex gap-2">
          <button style={{ borderColor: c.ink, color: c.ink, fontFamily: fontB, borderRadius: 2 }} className="px-4 py-2 text-[11px] uppercase tracking-wider border hover:bg-white">
            Request Draw
          </button>
          <button style={{ background: c.accent, color: '#fff', fontFamily: fontB, borderRadius: 2 }} className="px-4 py-2 text-[11px] uppercase tracking-wider hover:opacity-90">
            New Project
          </button>
        </div>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <KpiBlock label="Available" value={KPIS.available} sub={KPIS.availableSub} />
        <KpiBlock label="Daily Interest" value={KPIS.dailyInterest} sub={KPIS.dailyInterestSub} info={TOOLTIP_COPY} highlight />
        <KpiBlock label="Bank-Ready" value={KPIS.bankReady} sub={KPIS.bankReadySub} />
        <KpiBlock label="Compliance" value={KPIS.compliance} sub={KPIS.complianceSub} />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div style={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 2 }}>
            <div className="px-5 py-4 flex items-end justify-between" style={{ borderBottom: `1px solid ${c.line}` }}>
              <div>
                <p style={{ fontFamily: fontB, color: c.muted }} className="text-[10px] uppercase tracking-[0.18em]">Schedule I</p>
                <h2 style={{ fontFamily: fontH, color: c.ink }} className="text-xl font-semibold">Active Loans</h2>
              </div>
              <p style={{ fontFamily: fontM, color: c.muted }} className="text-[11px] tabular-nums">{MOCK_PROJECTS.length} positions</p>
            </div>
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: `1px solid ${c.line}`, borderTop: `2px double ${c.line}` }}>
                  {['Project', 'Loan', 'Term', 'Daily Interest', 'Score'].map((h, i) => (
                    <th key={h} style={{ fontFamily: fontB, color: c.muted }} className={`py-2 px-4 text-[10px] uppercase tracking-[0.16em] font-semibold ${i === 0 ? 'text-left' : 'text-right'}`}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MOCK_PROJECTS.map((p, idx) => (
                  <tr key={p.id} style={{ borderBottom: `1px solid ${c.line}`, background: idx % 2 ? 'rgba(245,241,234,0.4)' : c.surface }}>
                    <td className="py-3 px-4">
                      <p style={{ fontFamily: fontH, color: c.ink }} className="text-sm font-semibold">{p.name}</p>
                      <p style={{ fontFamily: fontB, color: c.muted }} className="text-[10px]">{p.address}</p>
                    </td>
                    <td style={{ fontFamily: fontM, color: c.ink }} className="py-3 px-4 text-sm text-right tabular-nums">${p.loan.toLocaleString()}</td>
                    <td style={{ fontFamily: fontM, color: c.ink }} className="py-3 px-4 text-sm text-right tabular-nums">{p.term ? `${p.term}mo${p.ext ? ' *' : ''}` : '—'}</td>
                    <td style={{ fontFamily: fontM, color: c.secondary }} className="py-3 px-4 text-sm text-right tabular-nums">${p.daily.toFixed(2)}</td>
                    <td style={{ fontFamily: fontM, color: c.ink }} className="py-3 px-4 text-sm text-right tabular-nums font-semibold">{p.score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ background: c.ink, color: c.bg, borderRadius: 2 }} className="mt-6 p-6">
            <p style={{ fontFamily: fontB, color: c.secondary }} className="text-[10px] uppercase tracking-[0.3em] mb-2">Analysis Suite</p>
            <h3 style={{ fontFamily: fontH }} className="text-2xl font-semibold mb-2">Underwrite a Deal</h3>
            <p style={{ fontFamily: fontB }} className="text-xs opacity-80 mb-4">Run cash-flow projections, ROI, IRR, and stress tests on potential deals before funding.</p>
            <button style={{ background: c.accent, color: '#fff', fontFamily: fontB, borderRadius: 2 }} className="px-4 py-2 text-[11px] uppercase tracking-wider font-semibold">Open Analysis Suite →</button>
          </div>
        </div>

        <div style={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 2 }}>
          <div className="px-5 py-4" style={{ borderBottom: `1px solid ${c.line}` }}>
            <p style={{ fontFamily: fontB, color: c.muted }} className="text-[10px] uppercase tracking-[0.18em]">Schedule II</p>
            <h2 style={{ fontFamily: fontH, color: c.ink }} className="text-xl font-semibold">Contractor Compliance</h2>
            <p style={{ fontFamily: fontB, color: c.muted }} className="text-[10px] mt-0.5">W-9 · COI · Lien Waiver</p>
          </div>
          <div>
            {MOCK_CONTRACTORS.map((cn, idx) => (
              <div key={cn.id} className="px-5 py-3 flex items-start justify-between gap-3" style={{ borderBottom: idx < MOCK_CONTRACTORS.length - 1 ? `1px solid ${c.line}` : 'none' }}>
                <div className="flex-1 min-w-0">
                  <p style={{ fontFamily: fontH, color: c.ink }} className="text-sm font-semibold truncate">{cn.name}</p>
                  <p style={{ fontFamily: fontB, color: c.muted }} className="text-[10px]">{cn.trade}</p>
                </div>
                <div className="flex gap-1">
                  <Pill label="W9" status={cn.w9} />
                  <Pill label="COI" status={cn.coi} />
                  <Pill label="Lien" status={cn.lien} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <p style={{ fontFamily: fontB, color: c.muted }} className="text-[10px] uppercase tracking-[0.3em] text-center mt-10 pt-6">
        Confidential · Verified Portfolio Data · Page 1 of 1
      </p>
    </div>
  );
};

// =============================================================
// THEME B — QuickBooks Authority (DrawReady blue + green compliance accents)
// =============================================================
const ThemeQB = () => {
  const c = {
    bg: '#FAF8F3', surface: '#FFFFFF', ink: '#1A1A1A', muted: '#595959',
    accent: DR_BLUE, accentDark: DR_BLUE_DARK,
    secondary: '#1F7A3D',  // green — only for compliance/positive states
    warning: '#D97706', danger: '#DC2626', line: 'rgba(26,26,26,0.12)',
  };
  const fontB = "'Inter', sans-serif";
  const fontM = "'IBM Plex Mono', monospace";

  const KpiBlock = ({ label, value, sub, info }) => (
    <div style={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 6 }} className="p-5">
      <p style={{ fontFamily: fontB, color: c.muted }} className="text-[10px] font-bold uppercase tracking-wider mb-1 inline-flex items-center">
        {label}
        {info && <InfoTip text={info} color={c.muted} hover={c.ink} />}
      </p>
      <p style={{ fontFamily: fontM, color: c.ink }} className="text-3xl font-medium tabular-nums">{value}</p>
      {sub && <p style={{ fontFamily: fontB, color: c.muted }} className="text-[11px] mt-1.5">{sub}</p>}
    </div>
  );

  const Pill = ({ label, status }) => {
    const styles = {
      approved:  { bg: c.secondary + '1A', color: c.secondary, label: 'Approved' },
      submitted: { bg: c.accent + '1A',    color: c.accent,    label: 'Submitted' },
      expiring:  { bg: c.warning + '1A',   color: c.warning,   label: 'Expiring' },
      awaiting:  { bg: c.danger + '1A',    color: c.danger,    label: 'Awaiting' },
      none:      { bg: c.line,             color: c.muted,     label: '—' },
    }[status] || { bg: c.line, color: c.muted, label: '—' };
    return (
      <span style={{ background: styles.bg, color: styles.color, fontFamily: fontB, borderRadius: 4 }} className="px-2 py-0.5 text-[10px] font-semibold">
        {label}: {styles.label}
      </span>
    );
  };

  return (
    <div style={{ background: c.bg, fontFamily: fontB, color: c.ink }} className="px-8 py-8 min-h-full">
      <header className="flex justify-between items-end pb-4 mb-6" style={{ borderBottom: `4px double ${c.line}` }}>
        <div>
          <h1 style={{ fontFamily: fontB, color: c.ink }} className="text-3xl font-bold tracking-tight leading-none">
            Command Center
          </h1>
          <p style={{ color: c.muted }} className="text-sm mt-1">Your portfolio at a glance — KPIs, loans, and compliance in one view.</p>
        </div>
        <div className="flex gap-2">
          <button style={{ borderColor: c.ink, color: c.ink, fontFamily: fontB, borderRadius: 6 }} className="px-4 py-2 text-sm font-medium border hover:bg-white">
            Request Draw
          </button>
          <button
            style={{ background: c.accent, color: '#fff', fontFamily: fontB, borderRadius: 6 }}
            className="px-4 py-2 text-sm font-medium"
            onMouseEnter={(e) => (e.currentTarget.style.background = c.accentDark)}
            onMouseLeave={(e) => (e.currentTarget.style.background = c.accent)}
          >
            + New Project
          </button>
        </div>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <KpiBlock label="Available" value={KPIS.available} sub={KPIS.availableSub} />
        <KpiBlock label="Daily Interest" value={KPIS.dailyInterest} sub={KPIS.dailyInterestSub} info={TOOLTIP_COPY} />
        <KpiBlock label="Bank-Ready" value={KPIS.bankReady} sub={KPIS.bankReadySub} />
        <KpiBlock label="Compliance" value={KPIS.compliance} sub={KPIS.complianceSub} />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <div style={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 6 }}>
            <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: `1px solid ${c.line}` }}>
              <h2 className="text-base font-semibold">Active Loans</h2>
              <p style={{ color: c.muted }} className="text-xs">{MOCK_PROJECTS.length} projects</p>
            </div>
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: `1px solid ${c.line}`, background: c.bg }}>
                  {['Project', 'Loan', 'Term', 'Daily Interest', 'Score'].map((h, i) => (
                    <th key={h} style={{ color: c.muted }} className={`py-2 px-4 text-[10px] font-bold uppercase tracking-wider ${i === 0 ? 'text-left' : 'text-right'}`}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MOCK_PROJECTS.map((p) => (
                  <tr key={p.id} className="transition-all" style={{ borderBottom: `1px solid ${c.line}`, borderLeft: '2px solid transparent' }}
                      onMouseEnter={(e) => (e.currentTarget.style.borderLeftColor = c.accent)}
                      onMouseLeave={(e) => (e.currentTarget.style.borderLeftColor = 'transparent')}>
                    <td className="py-3 px-4">
                      <p className="text-sm font-semibold">{p.name}</p>
                      <p style={{ color: c.muted }} className="text-[11px]">{p.address}</p>
                    </td>
                    <td style={{ fontFamily: fontM }} className="py-3 px-4 text-sm text-right tabular-nums">${p.loan.toLocaleString()}</td>
                    <td style={{ fontFamily: fontM }} className="py-3 px-4 text-sm text-right tabular-nums">{p.term ? `${p.term}mo${p.ext ? ' *' : ''}` : '—'}</td>
                    <td style={{ fontFamily: fontM, color: c.accent }} className="py-3 px-4 text-sm text-right font-medium tabular-nums">${p.daily.toFixed(2)}</td>
                    <td style={{ fontFamily: fontM }} className="py-3 px-4 text-sm text-right font-bold tabular-nums">{p.score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ background: c.surface, border: `2px solid ${c.accent}`, borderRadius: 6 }} className="mt-4 p-5 flex items-center gap-4">
            <div style={{ background: c.accent + '15', color: c.accent }} className="w-12 h-12 flex items-center justify-center rounded-md font-bold text-lg">⊞</div>
            <div className="flex-1">
              <p style={{ color: c.accent }} className="text-[10px] font-bold uppercase tracking-wider">Analysis Suite</p>
              <h3 className="text-base font-semibold">Underwrite a Deal</h3>
              <p style={{ color: c.muted }} className="text-xs">Run cash-flow projections, ROI, IRR, and stress tests on potential deals before funding.</p>
            </div>
            <button style={{ background: c.accent, color: '#fff', borderRadius: 6 }} className="px-4 py-2 text-sm font-medium">Open →</button>
          </div>
        </div>

        <div style={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 6 }}>
          <div className="px-5 py-3" style={{ borderBottom: `1px solid ${c.line}` }}>
            <h2 className="text-base font-semibold">Contractor Compliance</h2>
            <p style={{ color: c.muted }} className="text-[11px]">W-9 · COI · Lien Waiver</p>
          </div>
          <div>
            {MOCK_CONTRACTORS.map((cn, idx) => (
              <div key={cn.id} className="px-5 py-3" style={{ borderBottom: idx < MOCK_CONTRACTORS.length - 1 ? `1px solid ${c.line}` : 'none' }}>
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{cn.name}</p>
                    <p style={{ color: c.muted }} className="text-[11px]">{cn.trade}</p>
                  </div>
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  <Pill label="W9"  status={cn.w9} />
                  <Pill label="COI" status={cn.coi} />
                  <Pill label="Lien" status={cn.lien} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// =============================================================
// THEME C — Private Banker's Ledger (DrawReady blue + oxblood signature)
// =============================================================
const ThemeLedger = () => {
  const c = {
    bg: '#F8F5EE', surface: '#FFFFFF', ink: '#111827', muted: '#6B7280',
    accent: DR_BLUE, accentDark: DR_BLUE_DARK,
    secondary: '#7A1F2C',  // oxblood — signature accent for "official seal" elements
    success: '#059669', warning: '#D97706', danger: '#DC2626', line: 'rgba(17,24,39,0.18)',
  };
  const fontH = "'Source Serif 4', serif";
  const fontB = "'Inter', sans-serif";
  const fontM = "'IBM Plex Mono', monospace";

  const Pill = ({ label, status }) => {
    const styles = {
      approved:  { color: c.success, label: '✓' },
      submitted: { color: c.accent,  label: 'Sub' },
      expiring:  { color: c.warning, label: 'Exp' },
      awaiting:  { color: c.danger,  label: '!' },
      none:      { color: c.muted,   label: '—' },
    }[status] || { color: c.muted, label: '—' };
    return (
      <span style={{ borderColor: styles.color, color: styles.color, fontFamily: fontM, borderRadius: 0 }} className="px-1.5 py-0.5 text-[9px] uppercase border bg-white">
        {label} {styles.label}
      </span>
    );
  };

  return (
    <div style={{ background: c.bg, fontFamily: fontB, color: c.ink }} className="px-8 py-8 min-h-full">
      <header className="pb-4 mb-8" style={{ borderBottom: `1px solid ${c.secondary}` }}>
        <div className="flex justify-between items-end">
          <div>
            <p style={{ color: c.muted, fontFamily: fontM }} className="text-[10px] uppercase tracking-[0.3em] mb-1">
              Mods Enterprises  ·  Portfolio Ledger  ·  Q1 2026
            </p>
            <h1 style={{ fontFamily: fontH, color: c.ink }} className="text-5xl font-semibold leading-none">
              <span className="italic">Command</span> Center
            </h1>
          </div>
          <div className="flex gap-2">
            <button style={{ borderColor: c.ink, color: c.ink }} className="px-4 py-2 text-xs uppercase tracking-wider border hover:bg-white">Request Draw</button>
            <button style={{ background: c.accent, color: '#fff' }} className="px-4 py-2 text-xs uppercase tracking-wider hover:opacity-90">+ New Project</button>
          </div>
        </div>
      </header>

      {/* KPI ribbon */}
      <section style={{ background: c.surface, border: `1px solid ${c.line}` }} className="grid grid-cols-2 md:grid-cols-4 mb-8 divide-x">
        {[
          { label: 'Available',      value: KPIS.available,     sub: KPIS.availableSub },
          { label: 'Daily Interest', value: KPIS.dailyInterest, sub: KPIS.dailyInterestSub, info: TOOLTIP_COPY },
          { label: 'Bank-Ready',     value: KPIS.bankReady,     sub: KPIS.bankReadySub },
          { label: 'Compliance',     value: KPIS.compliance,    sub: KPIS.complianceSub },
        ].map((k) => (
          <div key={k.label} className="p-6" style={{ borderColor: c.line }}>
            <p style={{ fontFamily: fontH, color: c.muted }} className="text-[11px] uppercase tracking-[0.18em] mb-2 inline-flex items-center">
              {k.label}
              {k.info && <InfoTip text={k.info} color={c.muted} hover={c.ink} />}
            </p>
            <p style={{ fontFamily: fontM, color: c.ink }} className="text-3xl font-medium tabular-nums">{k.value}</p>
            <p style={{ color: c.muted }} className="text-[11px] mt-1.5">{k.sub}</p>
          </div>
        ))}
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div style={{ background: c.surface, border: `1px solid ${c.line}` }}>
            <div className="px-5 py-4 flex items-end justify-between" style={{ borderBottom: `2px double ${c.line}` }}>
              <div>
                <p style={{ fontFamily: fontM, color: c.muted }} className="text-[10px] uppercase tracking-[0.18em]">Schedule I</p>
                <h2 style={{ fontFamily: fontH, color: c.ink }} className="text-2xl font-semibold italic">Active Loans</h2>
              </div>
              <p style={{ fontFamily: fontM, color: c.muted }} className="text-[11px] tabular-nums">{MOCK_PROJECTS.length} positions</p>
            </div>
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: `1px solid ${c.line}` }}>
                  {['Project', 'Loan', 'Term', 'Daily Interest', 'Score'].map((h, i) => (
                    <th key={h} style={{ fontFamily: fontM, color: c.muted }} className={`py-2 px-4 text-[10px] uppercase tracking-[0.16em] font-medium ${i === 0 ? 'text-left' : 'text-right'}`}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MOCK_PROJECTS.map((p, idx) => (
                  <tr key={p.id} style={{ borderBottom: `1px solid ${c.line}`, background: idx % 2 ? c.bg : c.surface }}>
                    <td className="py-3 px-4">
                      <p style={{ fontFamily: fontH, color: c.ink }} className="text-sm font-semibold">{p.name}</p>
                      <p style={{ color: c.muted }} className="text-[10px]">{p.address}</p>
                    </td>
                    <td style={{ fontFamily: fontM }} className="py-3 px-4 text-sm text-right tabular-nums">${p.loan.toLocaleString()}</td>
                    <td style={{ fontFamily: fontM }} className="py-3 px-4 text-sm text-right tabular-nums">{p.term ? `${p.term}mo${p.ext ? ' *' : ''}` : '—'}</td>
                    <td style={{ fontFamily: fontM, color: c.accent }} className="py-3 px-4 text-sm text-right tabular-nums font-medium">${p.daily.toFixed(2)}</td>
                    <td style={{ fontFamily: fontM }} className="py-3 px-4 text-sm text-right font-semibold tabular-nums">{p.score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6" style={{ background: c.ink, borderTop: `2px solid ${c.secondary}`, borderBottom: `2px solid ${c.secondary}` }}>
            <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
              <div className="md:col-span-2">
                <p style={{ fontFamily: fontM, color: c.secondary }} className="text-[10px] uppercase tracking-[0.3em] mb-2">Analysis Suite</p>
                <h3 style={{ fontFamily: fontH, color: c.bg }} className="text-2xl font-semibold italic mb-1">Underwrite a Deal</h3>
                <p style={{ color: '#cbd5e1' }} className="text-xs">Cash-flow projections, ROI, IRR, and stress tests on potential deals before funding.</p>
              </div>
              <button style={{ background: c.accent, color: '#fff', fontFamily: fontB }} className="px-4 py-2.5 text-xs uppercase tracking-wider font-medium justify-self-end">Open Analysis Suite →</button>
            </div>
          </div>
        </div>

        <div style={{ background: c.surface, border: `1px solid ${c.line}` }}>
          <div className="px-5 py-4" style={{ borderBottom: `2px double ${c.line}` }}>
            <p style={{ fontFamily: fontM, color: c.muted }} className="text-[10px] uppercase tracking-[0.18em]">Schedule II</p>
            <h2 style={{ fontFamily: fontH, color: c.ink }} className="text-2xl font-semibold italic">Compliance</h2>
            <p style={{ color: c.muted }} className="text-[11px] mt-0.5">W-9 · COI · Lien Waiver</p>
          </div>
          <div>
            {MOCK_CONTRACTORS.map((cn, idx) => (
              <div key={cn.id} className="px-5 py-3 flex items-start justify-between gap-3" style={{ borderBottom: idx < MOCK_CONTRACTORS.length - 1 ? `1px solid ${c.line}` : 'none' }}>
                <div className="flex-1 min-w-0">
                  <p style={{ fontFamily: fontH, color: c.ink }} className="text-sm font-semibold truncate">{cn.name}</p>
                  <p style={{ color: c.muted }} className="text-[10px]">{cn.trade}</p>
                </div>
                <div className="flex gap-1">
                  <Pill label="W9"  status={cn.w9} />
                  <Pill label="COI" status={cn.coi} />
                  <Pill label="Lien" status={cn.lien} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// =============================================================
// PAGE
// =============================================================
const THEMES = [
  { id: 'jpm',    label: 'A. JPM Private Bank',          desc: 'Ivory paper · Source Serif headings · DrawReady blue primary · Brass figures · Letterhead feel',  Component: ThemeJPM },
  { id: 'qb',     label: 'B. QuickBooks Authority',      desc: 'Cream paper · Inter sans · DrawReady blue primary · Forest-green compliance · Double-rule dividers', Component: ThemeQB },
  { id: 'ledger', label: 'C. Private Banker\u2019s Ledger', desc: 'Cream · Source Serif italic · DrawReady blue primary · Oxblood signature · KPI ribbon (recommended)', Component: ThemeLedger },
];

export default function DashboardDesignPreview() {
  useDesignFonts();
  const [active, setActive] = useState('jpm');
  const ActiveTheme = THEMES.find((t) => t.id === active).Component;

  return (
    <div className="min-h-screen flex flex-col bg-slate-100" data-testid="design-preview-page">
      {/* Sticky theme switcher */}
      <div className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1800px] mx-auto px-6 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Design Preview</p>
              <h1 className="text-xl font-bold text-slate-900">Pick a direction</h1>
              <p className="text-xs text-slate-500 mt-0.5 max-w-3xl">
                Same DrawReady app — same logo, same blue brand color, same sidebar.
                Three different paper / typography / secondary accents. Pick one and the agent rolls it across the whole app.
              </p>
            </div>
            <div className="flex flex-wrap gap-1.5" role="tablist">
              {THEMES.map((t) => (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={active === t.id}
                  onClick={() => setActive(t.id)}
                  className={`px-3.5 py-2 text-xs font-medium rounded transition-all border ${
                    active === t.id
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white text-slate-700 border-slate-300 hover:border-slate-500'
                  }`}
                  data-testid={`theme-tab-${t.id}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-2 text-[11px] text-slate-500 italic">
            {THEMES.find((t) => t.id === active).desc}
          </div>
        </div>
      </div>

      {/* App shell: Sidebar + Theme content */}
      <div className="flex-1 flex" data-testid={`active-theme-${active}`}>
        <DrawReadySidebar activeTheme={THEMES.find((t) => t.id === active).label} />
        <main className="flex-1 overflow-x-auto">
          <ActiveTheme />
        </main>
      </div>

      <footer className="text-center text-xs text-slate-500 py-6 bg-white border-t border-slate-200">
        Design preview · DrawReady · Visual mock — pick a direction and I roll it across Dashboard, Project Hub, Reports, Compliance, Settings, Profile.
      </footer>
    </div>
  );
}
